import { AxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, Row } from 'react-table';

import AddBtn from '../../../../../../components/Buttons/AddBtn';
import Btn from '../../../../../../components/Buttons/Btn';
import Input from '../../../../../../components/Input';
import Modal from '../../../../../../components/Modal';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../../components/Notification';
import SelectAsyncJSX from '../../../../../../components/SelectAsync';
import SingleSelect from '../../../../../../components/SingleSelect';
import TablePaginator from '../../../../../../components/TablePaginator';
import { ActionButtonsContainer } from '../../../../../../components/TablePaginator/styles';
import HttpStatus from '../../../../../../enums/httpStatus';
import i18n from '../../../../../../i18n';
import { DeleteIcon } from '../../../../../../icons';
import {
  audienceAPI,
  audienceAutomatedAPI,
  audienceUsersAPI,
  customersApi,
  sellersApi,
} from '../../../../../../services/requests';
import { Audience, AudienceParticipant } from '../../../../../../types';
import {
  getOptionsFromCustomers,
  getOptionsFromSellers,
  getOptionsFromUsers,
} from '../../../../../../utils/getSelectOptions';
import * as s from './styles';
import RemoveAudienceParticipantsModal, {
  AudienceParticipantTypeToRemove,
} from '../ModalRemoveMany/RemoveAudienceParticipantsModal';

type ParticipantTypes = 'seller' | 'customer' | 'user' | 'allSellers' | 'allCustomers' | 'allUsers';
type AutomatedParticipantType = 'allSellers' | 'allCustomers' | 'allUsers';

const isAutomatedParticipantType = (type: string): type is AutomatedParticipantType =>
  type === 'allSellers' || type === 'allCustomers' || type === 'allUsers';

type Props = {
  closeModal: () => void;
  callback: () => void;
  selectedItem: Audience;
  availableTypes?: ParticipantTypes[] | null;
  allowAutomated?: boolean;
  onParticipantsChanged?: (
    audienceId: number,
    removedTypes: AudienceParticipantTypeToRemove[],
  ) => void;
};

type Option = {
  value: any;
  label: string;
};

type ColumnType = AudienceParticipant;

const ModalEditJSX = ({
  closeModal,
  callback,
  availableTypes,
  selectedItem,
  onParticipantsChanged,
  allowAutomated = false,
}: Props) => {
  const { t } = useTranslation();

  const [waitingReq, setWaitingReq] = useState(false);
  const [waitingGetParticipants] = useState(false);
  const [showRemoveManyModal, setShowRemoveManyModal] = useState(false);

  const [description, setDescription] = useState(selectedItem.description);
  const selectedAutomationTypes = selectedItem.automationTypes ?? [];
  const selectedIsAutomated =
    selectedItem.isAutomated === true || selectedAutomationTypes.length > 0;
  const [isAutomated, setIsAutomated] = useState(selectedIsAutomated);

  const [type, setType] = useState(
    selectedIsAutomated
      ? { value: 'allSellers', label: t('allSellers') }
      : { value: 'seller', label: t('specificSellers') },
  );
  const [participant, setParticipant] = useState<Option[]>([]);
  const [participantName, setParticipantName] = useState('');

  const [participants, setParticipants] = useState<ColumnType[]>(
    selectedIsAutomated
      ? selectedAutomationTypes
        .filter((automationType): automationType is AutomatedParticipantType =>
          isAutomatedParticipantType(automationType),
        )
        .map((automationType) => ({
          type: automationType,
          code: null,
          name: t(automationType) as string,
        }))
      : (selectedItem.participants ?? []).filter(
        (participant) => String(participant.name ?? '').trim().length > 0,
      ),
  );

  const reqEdit = async () => {
    if (description.length <= 0) {
      frontendNotification({
        message: t('invalidDescription'),
        type: 'warning',
      });
      return () => null;
    }

    if (participants.length <= 0) {
      frontendNotification({
        message: t('addAtLeastOneParticipant'),
        type: 'warning',
      });
      return () => null;
    }

    const automationTypes = participants
      .filter((participantIncluded) => isAutomatedParticipantType(participantIncluded.type))
      .map((participantIncluded) => participantIncluded.type);

    if (isAutomated && automationTypes.length <= 0) {
      frontendNotification({
        message: t('addAtLeastOneParticipant'),
        type: 'warning',
      });
      return () => null;
    }

    setWaitingReq(true);

    const requestApi = isAutomated ? audienceAutomatedAPI : audienceAPI;
    requestApi.query = '';
    const source = requestApi.axios.CancelToken.source();

    try {
      const res = isAutomated
        ? await audienceAutomatedAPI.update(
          { description, automationTypes },
            selectedItem.id as number,
        )
        : await audienceAPI.update(
          {
            description,
            participants,
          },
            selectedItem.id as number,
        );

      if (res.status === HttpStatus.OK) {
        setWaitingReq(false);
        frontendNotification({
          message: t('successfullyCreated'),
          type: 'success',
        });

        closeModal();
        callback();
      } else {
        throw res;
      }

      setWaitingReq(false);
    } catch (err) {
      if (!requestApi.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
      setWaitingReq(false);
    }

    return () => {
      source.cancel('Component got unmounted');
    };
  };

  const handleAutomatedChange = (checked: boolean) => {
    setIsAutomated(checked);
    setParticipant([]);
    setParticipantName('');

    if (checked) {
      setType({ value: 'allSellers', label: t('allSellers') });
      setParticipants((currentParticipants) =>
        currentParticipants.filter((participantIncluded) =>
          isAutomatedParticipantType(participantIncluded.type),
        ),
      );
      return;
    }

    setType({ value: 'seller', label: t('specificSellers') });
  };

  const handleAddBtn = () => {
    const isAllParticipantsType =
      type.value === 'allSellers' || type.value === 'allCustomers' || type.value === 'allUsers';

    if (isAllParticipantsType) {
      if (participants.some((participantIncluded) => participantIncluded.type === type.value)) {
        frontendNotification({
          message: t('alreadyExists'),
          type: 'warning',
        });
        return;
      }

      setParticipants([
        {
          type: type.value,
          code: null,
          name: t(type.value) as string,
        },
        ...participants,
      ]);
      setParticipant([]);
      setParticipantName('');
      return;
    }

    if (participant.length === 0) {
      frontendNotification({
        message: t('chooseTheParticipant'),
        type: 'warning',
      });
      return;
    }

    const participantsToAdd: ColumnType[] = participant.map((participantSelected) => {
      const name = participantSelected.label.replace(`${participantSelected.value} - `, '');

      return {
        type: type.value,
        code: participantSelected.value,
        name: participant.length === 1 ? participantName : name,
      };
    });

    if (participantsToAdd.some((participantToAdd) => participantToAdd.name.length <= 1)) {
      frontendNotification({
        message: t('invalidName'),
        type: 'warning',
      });
      return;
    }

    const newParticipants = participantsToAdd.filter(
      (participantToAdd) =>
        !participants.some(
          (participantIncluded) =>
            participantIncluded.type === participantToAdd.type &&
            participantIncluded.code === participantToAdd.code,
        ),
    );

    if (newParticipants.length < participantsToAdd.length) {
      frontendNotification({
        message: t('alreadyExists'),
        type: 'warning',
      });
    }

    if (newParticipants.length === 0) {
      return;
    }

    setParticipants([...newParticipants, ...participants]);
    setParticipant([]);
    setParticipantName('');
  };

  const handleSelectParticipant = (participantSelected: Option[] | null) => {
    const selectedParticipants = participantSelected ?? [];

    setParticipant(selectedParticipants);
    setParticipantName(
      selectedParticipants.length === 1
        ? selectedParticipants[0].label.replace(`${selectedParticipants[0].value} - `, '')
        : '',
    );
  };

  const removeParticipant = (partiSelected: ColumnType) => {
    const newParti = participants.filter(
      (participantIncluded) =>
        JSON.stringify(participantIncluded) !== JSON.stringify(partiSelected),
    );

    setParticipants(newParti);
  };

  const handleRemoveManySuccess = (removedTypes: AudienceParticipantTypeToRemove[]) => {
    const remainingParticipants = participants.filter(
      (currentParticipant) =>
        !removedTypes.includes(currentParticipant.type as AudienceParticipantTypeToRemove),
    );

    setParticipants(remainingParticipants);
    onParticipantsChanged?.(selectedItem.id as number, removedTypes);
  };

  const actionsButtons = (row: Row<ColumnType>) => (
    <ActionButtonsContainer type={3}>
      <DeleteIcon size="1.5rem" title={t('edit')} onClick={() => removeParticipant(row.original)} />
    </ActionButtonsContainer>
  );

  const handleTypeColumn = (row: Row<ColumnType>) => <p>{t(row.original.type)}</p>;

  const columns: Column<ColumnType>[] = useMemo(
    () => [
      {
        Header: t('type') as string,
        Cell: ({ row }: any) => handleTypeColumn(row),
      },
      {
        Header: t('code') as string,
        accessor: 'code',
      },
      {
        Header: t('name') as string,
        accessor: 'name',
      },
      {
        id: 'actionsbtn',
        Cell: ({ row }: any) => actionsButtons(row),
      },
    ],
    [i18n.language, participants],
  );

  const handlePartTypeOptions = (): Option[] => {
    const options = [
      { value: 'seller', label: t('specificSellers') },
      { value: 'customer', label: t('specificCustomers') },
      { value: 'user', label: t('specificUsers') },
      { value: 'allSellers', label: t('allSellers') },
      { value: 'allCustomers', label: t('allCustomers') },
      { value: 'allUsers', label: t('allUsers') },
    ];

    if (availableTypes == null) {
      return isAutomated
        ? options.filter((option) => isAutomatedParticipantType(option.value))
        : options;
    }

    const sellerAvailable =
      availableTypes.includes('seller') || availableTypes.includes('allSellers');
    const customerAvailable =
      availableTypes.includes('customer') || availableTypes.includes('allCustomers');
    const userAvailable = availableTypes.includes('user') || availableTypes.includes('allUsers');

    const filteredOptions = options.filter((option) => {
      if (option.value === 'seller' || option.value === 'allSellers') {
        return sellerAvailable;
      }

      if (option.value === 'customer' || option.value === 'allCustomers') {
        return customerAvailable;
      }

      return userAvailable;
    });

    return isAutomated
      ? filteredOptions.filter((option) => isAutomatedParticipantType(option.value))
      : filteredOptions;
  };

  const handleRequestToSelectAsync = () => {
    switch (type.value) {
      case 'seller':
        return sellersApi;
      case 'customer':
        return customersApi;
      case 'user':
        return audienceUsersAPI;
      default:
        return sellersApi;
    }
  };

  const createOptionsToSelectAsync = () => {
    switch (type.value) {
      case 'seller':
        return getOptionsFromSellers;
      case 'customer':
        return getOptionsFromCustomers;
      case 'user':
        return getOptionsFromUsers;
      default:
        return getOptionsFromSellers;
    }
  };

  useEffect(() => {
    setParticipant([]);
    setParticipantName('');
  }, [type]);

  return (
    <>
      <Modal
        title={t('editAudience')}
        showModal
        closeModal={closeModal}
        cancelButton={closeModal}
        action={() => reqEdit()}
        isLoading={waitingReq || waitingGetParticipants}
        bodyStyle={participants.length >= 3 ? { overflowX: 'scroll' } : {}}
      >
        <s.Container>
          <s.Line className="first-line">
            <s.Item>
              <p>{t('description')}</p>
              <Input
                value={description}
                onChange={(e: any) => setDescription(e.target.value)}
                disabled={waitingReq}
              />
            </s.Item>
          </s.Line>

          {(allowAutomated || isAutomated) && (
            <s.Line>
              <s.Item>
                <label style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={isAutomated}
                    onChange={(event) => handleAutomatedChange(event.target.checked)}
                    disabled={waitingReq}
                    style={{ marginRight: '0.5rem' }}
                  />
                  {t('automatedAudience')}
                </label>
                {isAutomated && <p>{t('automatedAudienceHelp')}</p>}
              </s.Item>
            </s.Line>
          )}

          <s.Line>
            <s.Item>
              <p>{t('type')}</p>
              <SingleSelect
                value={type}
                options={handlePartTypeOptions()}
                onChange={setType as any}
                disabled={waitingReq}
                style={{ minWidth: '150px' }}
              />
            </s.Item>

            {(type.value === 'seller' || type.value === 'customer' || type.value === 'user') && (
              <>
                <s.Item>
                  <p>{t('participant')}</p>

                  <SelectAsyncJSX
                    value={participant}
                    onChange={handleSelectParticipant}
                    request={handleRequestToSelectAsync()}
                    reqResponseToOption={createOptionsToSelectAsync()}
                    disabled={waitingReq}
                    placeholder={t('participant')}
                    cacheUniqs={[type]}
                    isMulti
                  />
                </s.Item>

                {participant.length <= 1 && (
                  <s.Item>
                    <p>{t('name')}</p>

                    <Input
                      value={participantName}
                      onChange={(e: any) => setParticipantName(e.target.value)}
                    />
                  </s.Item>
                )}
              </>
            )}

            <s.Item className="add-btn">
              <AddBtn onClick={() => handleAddBtn()} />
            </s.Item>
          </s.Line>

          {!isAutomated && (
            <s.Line>
              <Btn
                text={t('removeManyParticipants')}
                type="outlined"
                onClick={() => setShowRemoveManyModal(true)}
                disabled={waitingReq}
              />
            </s.Line>
          )}

          <s.Line>
            <TablePaginator
              data={participants}
              columns={columns}
              globalFiltering
              style={{ width: '100%', marginTop: '2rem' }}
            />
          </s.Line>
        </s.Container>
      </Modal>

      {showRemoveManyModal && (
        <RemoveAudienceParticipantsModal
          audienceId={selectedItem.id as number}
          closeModal={() => setShowRemoveManyModal(false)}
          onSuccess={handleRemoveManySuccess}
        />
      )}
    </>
  );
};

ModalEditJSX.defaultProps = {
  availableTypes: null,
  allowAutomated: false,
};

export default ModalEditJSX;
